import { Data, Option, Array as A, Record as R, pipe } from 'effect';

/**
 * One input of an access decision. CASL answers only "can" or "cannot"; Loading keeps "not known yet" apart from
 * "forbidden", and Failed keeps an input that will not arrive apart from one still loading.
 */
export type AccessInput<Value> = Data.TaggedEnum<{
  Loading: {};
  Failed: { readonly error: unknown };
  Ready: { readonly value: Value };
}>;

interface AccessInputDefinition extends Data.TaggedEnum.WithGenerics<1> {
  readonly taggedEnum: AccessInput<this['A']>;
}

export const AccessInput = Data.taggedEnum<AccessInputDefinition>();

export const accessInputFromRedux = <Value>(value: Value): AccessInput<Value> => AccessInput.Ready({ value });

/** The value once known; none while it loads or after it failed, for a reader that shows nothing until then. */
export const accessInputToOption = <Value>(input: AccessInput<Value>): Option.Option<Value> =>
  AccessInput.$match(input, {
    Ready: ({ value }) => Option.some(value),
    Loading: () => Option.none(),
    Failed: () => Option.none(),
  });

export const mapAccessInput = <Value, Result>(
  input: AccessInput<Value>,
  f: (value: Value) => Result,
): AccessInput<Result> =>
  AccessInput.$match(input, {
    Loading: () => AccessInput.Loading(),
    Failed: ({ error }) => AccessInput.Failed({ error }),
    Ready: ({ value }) => AccessInput.Ready({ value: f(value) }),
  });

type ValuesOf<Inputs> = { readonly [K in keyof Inputs]: Inputs[K] extends AccessInput<infer Value> ? Value : never };

export const combineAccessInputs = <Inputs extends Record<string, AccessInput<unknown>>>(
  inputs: Inputs,
): AccessInput<ValuesOf<Inputs>> => {
  const all = Object.values(inputs);
  const failed = A.findFirst(all, AccessInput.$is('Failed'));
  if (failed._tag === 'Some') return AccessInput.Failed({ error: failed.value.error });
  if (A.some(all, AccessInput.$is('Loading'))) return AccessInput.Loading();
  return AccessInput.Ready({
    // TypeScript cannot carry a generic mapped record through R.map; every input is Ready here.
    value: pipe(
      inputs,
      R.map((input) => (AccessInput.$is('Ready')(input) ? input.value : undefined)),
    ) as ValuesOf<Inputs>,
  });
};
